import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { ClipboardList } from 'lucide-react'
import type { DocumentPreviewData } from '@/types/Content'
import { PREVIEW_LAZY_LOAD_MARGIN } from '@/constants/Preview'
import './document-preview-grid.css'

type DocumentPreviewGridProps = {
	preview: DocumentPreviewData
	playCount: number
}

export function DocumentPreviewGrid({ preview, playCount }: DocumentPreviewGridProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [isVisible, setIsVisible] = useState(false)

	// Lazy loading with IntersectionObserver
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry?.isIntersecting) {
					setIsVisible(true)
					observer.disconnect()
				}
			},
			{ rootMargin: PREVIEW_LAZY_LOAD_MARGIN },
		)

		if (containerRef.current) {
			observer.observe(containerRef.current)
		}

		return () => observer.disconnect()
	}, [])

	// Fallback: No sections - show gradient with icon
	if (!preview.sections || preview.sections.length === 0) {
		return (
			<div className="document-preview-empty">
				<ClipboardList className="document-preview-empty-icon" />
			</div>
		)
	}

	// Get the template type label
	const templateLabel = {
		down_and_distance: 'Down & Distance',
		field_zones: 'Field Zones',
		personnel: 'Personnel',
		custom: 'Custom',
	}[preview.templateType] ?? 'Document'

	// Calculate max dots per section (limit to prevent overcrowding)
	const maxDotsPerRow = 6
	const maxRows = 3

	return (
		<div ref={containerRef} className="document-preview-grid">
			{isVisible && (
				<>
					<div className="document-preview-sections">
						{preview.sections.slice(0, 4).map((section) => (
							<div key={section.id} className="document-preview-section">
								<div
									className="document-preview-section-header"
									style={{
										backgroundColor: section.color ?? 'var(--color-border)',
									}}
								>
									<span className="document-preview-section-name">
										{section.name}
									</span>
								</div>
								<div className="document-preview-section-plays">
									{renderPlayDots(section.playTypes, maxDotsPerRow, maxRows)}
								</div>
							</div>
						))}
					</div>
					<div className="document-preview-template-label">
						{templateLabel}
					</div>
				</>
			)}
		</div>
	)
}

/**
 * Render play type dots with color coding
 * Red = run, Blue = pass, Gray = unknown
 */
function renderPlayDots(
	playTypes: Array<'run' | 'pass' | null> | undefined,
	maxPerRow: number,
	maxRows: number,
): React.JSX.Element {
	// Handle undefined or empty playTypes
	if (!playTypes || playTypes.length === 0) {
		return <div className="document-preview-dots" />
	}

	const maxDots = maxPerRow * maxRows
	const dotsToShow = playTypes.slice(0, maxDots)
	const remaining = playTypes.length - maxDots

	return (
		<div className="document-preview-dots">
			{dotsToShow.map((playType, index) => (
				<span
					key={index}
					className={`document-preview-dot ${getPlayTypeDotClass(playType)}`}
				/>
			))}
			{remaining > 0 && (
				<span className="document-preview-more">+{remaining}</span>
			)}
		</div>
	)
}

function getPlayTypeDotClass(playType: 'run' | 'pass' | null): string {
	switch (playType) {
		case 'run':
			return 'document-preview-dot-run'
		case 'pass':
			return 'document-preview-dot-pass'
		default:
			return 'document-preview-dot-neutral'
	}
}
